interface ThemeColors {
  primaryRed: string;
  lightGrayBackground: string;
  whiteCardBackground: string;
  darkText: string;
  lightText: string;
  greenSuccess: string;
  blueUpload: string;
  borderColor: string;
  darkGrayBackground: string;
  lightPrimaryRed: string;
  taskCardBackground: string;
  secondaryOrange: string; // New: Secondary orange color
  headerGradient: string; // New: Header gradient string
  veryDarkBackground: string; // Added new color
}

interface ThemeFonts {
  primary: string;
  heading: string;
}

interface ThemeSpacing {
  small: string;
  medium: string;
  large: string;
  extraLarge: string;
}

export interface Theme {
  name: string;
  colors: ThemeColors;
  fonts: ThemeFonts;
  spacing: ThemeSpacing;
  borderRadius: string;
  boxShadow: string;
  unreadNotificationBackground?: string; // Optional for themes that define it
}

const lightTheme: Theme = {
  name: 'Light',
  colors: {
    primaryRed: '#FF4D4D',
    lightGrayBackground: '#F8F8F8',
    whiteCardBackground: '#FFFFFF',
    darkText: '#333333',
    lightText: '#666666',
    greenSuccess: '#4CAF50',
    blueUpload: '#007BFF',
    borderColor: '#E0E0E0',
    darkGrayBackground: '#6c757d',
    lightPrimaryRed: '#FF8080',
    taskCardBackground: '#FFFFFF',
    secondaryOrange: '#FFA500', // New: Secondary orange color for light theme
    headerGradient: 'linear-gradient(to right, #FF4D4D, #FFA500)', // New: Header gradient for light theme
    veryDarkBackground: '#1A1A1A', // Added for consistency
  },
  fonts: {
    primary: 'Arial, sans-serif',
    heading: 'Roboto, sans-serif',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px',
  },
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // More prominent shadow
};

const darkTheme: Theme = {
  name: 'Dark',
  colors: {
    primaryRed: '#FF4D4D',
    lightGrayBackground: '#333333', // Darker background
    whiteCardBackground: '#424242', // Darker card background
    darkText: '#E0E0E0', // Lighter text for dark background
    lightText: '#B0B0B0', // Lighter secondary text
    greenSuccess: '#66BB6A', // Adjusted for dark mode visibility
    blueUpload: '#64B5F6', // Adjusted for dark mode visibility
    borderColor: '#555555', // Darker border
    darkGrayBackground: '#3A3A3A', // A darker gray for dark theme
    lightPrimaryRed: '#FF8080', // Using the same light red for dark theme for now
    taskCardBackground: '#424242', // Using the same as whiteCardBackground for dark theme
    secondaryOrange: '#FF8C00', // New: Secondary orange color for dark theme
    headerGradient: 'linear-gradient(to right, #FF4D4D, #FF8C00)', // New: Header gradient for dark theme
    veryDarkBackground: '#1A1A1A', // Added for consistency
  },
  fonts: {
    primary: 'Arial, sans-serif',
    heading: 'Roboto, sans-serif',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px',
  },
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // More prominent shadow
};

const blueTheme: Theme = {
  name: 'Blue',
  colors: {
    primaryRed: '#2196F3', // Primary blue
    lightGrayBackground: '#E3F2FD',
    whiteCardBackground: '#FFFFFF',
    darkText: '#212121',
    lightText: '#757575',
    greenSuccess: '#4CAF50',
    blueUpload: '#1976D2',
    borderColor: '#BBDEFB',
    darkGrayBackground: '#6c757d',
    lightPrimaryRed: '#FF8080', // Using the same light red for blue theme for now
    taskCardBackground: '#FFFFFF',
    secondaryOrange: '#1976D2', // Changed to darker blue
    headerGradient: 'linear-gradient(to right, #2196F3, #1976D2)', // Changed to darker blue gradient
    veryDarkBackground: '#1A1A1A', // Added for consistency
  },
  fonts: {
    primary: 'Arial, sans-serif',
    heading: 'Roboto, sans-serif',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px',
  },
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // More prominent shadow
};

const midnightBlueTheme: Theme = {
  name: 'Midnight Blue',
  colors: {
    primaryRed: '#FF4D4D',
    lightGrayBackground: '#1A202C', // Dark blue-gray background
    whiteCardBackground: '#2D3748', // Slightly lighter dark blue-gray for cards
    darkText: '#E2E8F0', // Light text for dark background
    lightText: '#A0AEC0', // Lighter secondary text
    greenSuccess: '#68D391', // Adjusted green for dark mode
    blueUpload: '#63B3ED', // Adjusted blue for dark mode
    borderColor: '#4A5568', // Darker border
    darkGrayBackground: '#2A323F', // A darker gray for midnight blue theme
    lightPrimaryRed: '#FF8080', // Using the same light red for midnight blue theme for now
    taskCardBackground: '#2D3748', // Using the same as whiteCardBackground for midnight blue theme
    secondaryOrange: '#FF7F50', // New: Secondary orange color for midnight blue theme
    headerGradient: 'linear-gradient(to right, #FF4D4D, #FF7F50)', // New: Header gradient for midnight blue theme
    veryDarkBackground: '#1A1A1A', // Added for consistency
  },
  fonts: {
    primary: 'Arial, sans-serif',
    heading: 'Roboto, sans-serif',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px',
  },
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)', // More prominent shadow
};

const sandTheme: Theme = {
  name: 'Sand',
  colors: {
    primaryRed: '#B36D60', // New accent color from user feedback
    lightGrayBackground: '#DED8C9', // The main sandy background color
    whiteCardBackground: '#F5F2EB', // The off-white color of the walls and card backgrounds
    darkText: '#3D3A32', // Dark text for primary content
    lightText: '#7A756C', // Lighter text for secondary content
    greenSuccess: '#6B8E23', // Olive green for success
    blueUpload: '#87CEEB', // Sky blue for upload
    borderColor: '#C7C2B5', // A subtle border color
    darkGrayBackground: '#6c757d',
    lightPrimaryRed: '#FF8080', // Using the same light red for sand theme for now
    taskCardBackground: '#F5F2EB', // Match whiteCardBackground for sand theme
    secondaryOrange: '#D2B48C', // New: Secondary orange color for sand theme
    headerGradient: 'linear-gradient(to right, #B36D60, #D2B48C)', // New: Header gradient for sand theme
    veryDarkBackground: '#1A1A1A', // Added for consistency
  },
  fonts: {
    primary: 'Arial, sans-serif',
    heading: 'Roboto, sans-serif',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px',
  },
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // More prominent shadow
};

export const predefinedThemes: Theme[] = [
  lightTheme,
  darkTheme,
  blueTheme,
  midnightBlueTheme,
  sandTheme,
];

export default predefinedThemes;
